import { useEffect, useRef, useState } from "react";
import { FaFilePdf, FaUpload, FaTimes } from "react-icons/fa";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { showError, showSuccess } from "../../../../utils/toastUtils";
import uploadFileToCloudinary from "../../../../utils/uploadFileToCloudinary";
import { downloadFile } from "../../../../utils/helpers";

const PdfUploadSection = ({
  buttonText = "Upload Brochure",
  maxSizeMB = 10,
  onFileSelect,
  prefillData,
}) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileUrl, setFileUrl] = useState(prefillData || "");
  const [isDownloading, setIsDownloading] = useState(false);

  // console.log("fileurl:", prefillData);

  useEffect(() => {
    if (prefillData) {
      setFileUrl(prefillData);
    }
  }, [prefillData]);

  useEffect(() => {
    const handleUpload = async () => {
      if (!selectedFile) return;

      try {
        setIsUploading(true);
        setUploadProgress(0);

        const { uploadPromise } = uploadFileToCloudinary(selectedFile, (p) =>
          setUploadProgress(p)
        );

        const result = await uploadPromise;

        setIsUploading(false);
        setFileUrl(result.url);
        showSuccess("PDF uploaded successfully");

        // Send uploaded file info to parent
        onFileSelect?.(result.url);
      } catch (err) {
        setIsUploading(false);
        showError(err.message || "Failed to upload file");
      }
    };

    handleUpload();
  }, [selectedFile]);

  const handleFileClick = () => {
    setFileUrl(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        showError("Only PDF files are allowed!");
        return;
      }
      if (file.size > maxSizeMB * 1024 * 1024) {
        showError(`File size exceeds ${maxSizeMB}MB`);
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setFileUrl(""); // Clear the URL
    onFileSelect?.(""); // Notify parent that file is removed
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const handleDownload = async (url) => {
    try {
      setIsDownloading(true);
      await downloadFile(url);
    } catch (err) {
      showError("Failed to download file");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="w-full">
      <div
        onClick={() => {
          if (fileUrl && !selectedFile) {
            handleDownload(fileUrl);
          } else {
            handleFileClick();
          }
        }}
        className="mt-4 border-2 border-dashed border-gray-300 rounded-xl px-6 py-6 text-center
                  hover:border-primary transition-colors cursor-pointer relative bg-white shadow-sm"
      >
        <input
          type="file"
          accept="application/pdf"
          ref={fileInputRef}
          onChange={handleFileChange}
          disabled={fileUrl && !selectedFile}
          className="hidden"
        />

        {!fileUrl ? (
          isUploading ? (
            <div className="flex items-center justify-center gap-3 py-3 relative">
              <AiOutlineLoading3Quarters className="w-6 h-6 text-primary animate-spin" />
              <p className="text-sm text-text-muted">
                Uploading... {uploadProgress}%
              </p>
            </div>
          ) : (
            <div className="py-3 flex flex-col items-center justify-center">
              <FaFilePdf className="text-primary w-12 h-12 mb-3" />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFileClick();
                }}
                className="flex items-center gap-2 text-primary font-medium hover:underline"
              >
                <FaUpload className="w-4 h-4" />
                {buttonText}
              </button>
              <p className="text-xs text-text-muted mt-2">
                Max size {maxSizeMB}MB | Format: PDF
              </p>
            </div>
          )
        ) : (
          <div className="flex items-center justify-center gap-3 py-3 relative group">
            {isUploading ? (
              <>
                <AiOutlineLoading3Quarters className="w-6 h-6 text-primary animate-spin" />
                <p className="text-sm text-text-muted">
                  Uploading... {uploadProgress}%
                </p>
              </>
            ) : (
              <>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(fileUrl);
                  }}
                  className="flex items-center justify-center gap-2"
                >
                  <FaFilePdf className="text-red-500 w-10 h-10" />
                  {isDownloading ? (
                    <div className="flex items-center gap-2 text-sm text-text-muted">
                      <AiOutlineLoading3Quarters className="animate-spin w-4 h-4" />
                      Downloading...
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-text-primary  hover:underline cursor-pointer">
                      {selectedFile?.name || "Brochure.pdf"}
                    </p>
                  )}

                  {/* Remove Button */}
                  <button
                    onClick={handleRemove}
                    className="absolute top-0 right-0 p-1 bg-black/60 hover:bg-red-600/80 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <FaTimes className="w-3 h-3" />
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfUploadSection;
